import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ESRI } from '@rsos/maps/constants/mapProviders';
import { MARKER_TYPE_GRAPHIC } from '@rsos/maps/constants/markerTypes';
import { createMarker } from '@rsos/maps/interface/markers';
import { createGraphicsLayer } from '@rsos/maps/providers/esri/graphics/layers';
import w3wIcon from '../../Map/markers/assets/w3w/w3w.svg';

export const W3WMarkers = ({ map }) => {
  const [W3WMarker, setW3WMarker] = useState(null);
  const [W3WMarkerLayer, setW3WMarkerLayer] = useState({});
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [
    W3WMarkerLayerHoverHandler,
    setW3WMarkerLayerHoverHandler,
  ] = useState();

  const what3words = useSelector(state => state.what3words.searched);
  const hasW3w = !!what3words?.coordinates;

  // remove the layer from the map instance
  const clearMarker = async () => {
    await new Promise(resolve => {
      switch (map.mapProvider) {
        case ESRI: {
          map.mapInstance.map.remove(W3WMarkerLayer);
          break;
        }
      }
      resolve();
    });
  };

  // Draw a single marker for w3w
  const drawMarker = () => {
    if (hasW3w && what3words) {
      switch (map.mapProvider) {
        case ESRI: {
          createGraphicsLayer({ config: { id: 'w3w-marker-layer' } }).then(
            layer => {
              // Add graphics layer at the end of layers collection to ensure markers are displayed
              // on top of other layer types.
              // https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#add
              map.mapInstance.map.layers.push(layer);
              const words = what3words.words;
              const latlng = {
                lat: what3words.coordinates.lat,
                lng: what3words.coordinates.lng,
              };
              return createMarker({
                mapObject: map,
                options: {
                  point: latlng,
                  icon: w3wIcon,
                  iconSize: {
                    height: 36,
                    width: 24,
                  },
                  custom: {
                    words,
                  },
                  layer,
                  popupTemplate: false,
                },
                markerType: MARKER_TYPE_GRAPHIC,
              })
                .then(resolvedMarker => {
                  layer.add(resolvedMarker.instance);
                  return resolvedMarker;
                })
                .then(resolvedMarker => {
                  setW3WMarkerLayer(layer);
                  setW3WMarker(resolvedMarker);
                  map.mapInstance.view.goTo({
                    center: [latlng.lng, latlng.lat],
                  });
                });
            },
          );
          break;
        }
        default:
          return null;
      }
    } else {
      return null;
    }
  };

  const handleMarkerHover = () => {
    switch (map.mapProvider) {
      case ESRI: {
        if (W3WMarkerLayerHoverHandler) {
          W3WMarkerLayerHoverHandler.remove();
        }
        if (W3WMarkerLayer?.graphics?.items.length) {
          const view = map.mapInstance.view;
          const hoverHandler = view.on('pointer-move', evt => {
            const opts = {
              include: W3WMarkerLayer,
            };
            view.hitTest(evt, opts).then(response => {
              if (response.results.length) {
                if (
                  W3WMarker.instance.uid === response?.results[0]?.graphic?.uid
                ) {
                  setHoveredMarker(W3WMarker);
                }
              } else {
                setHoveredMarker(null);
              }
            });
          });
          setW3WMarkerLayerHoverHandler(hoverHandler);
        }
        break;
      }
      default:
        return null;
    }
  };
  useEffect(() => {
    if (hoveredMarker) {
      map.mapInstance.view.popup.content = `<div class="esri-popup-w3w">w3w <p>${hoveredMarker.instance.point.y}, ${hoveredMarker.instance.point.x}</p></div>`;
      map.mapInstance.view.popup.location = hoveredMarker.instance.point;
      map.mapInstance.view.popup.visible = true;
      map.mapInstance.view.popup.alignment = 'top-center';
    } else {
      map.mapInstance.view.popup.location = null;
      map.mapInstance.view.popup.content = null;
      map.mapInstance.view.popup.visible = false;
    }
  }, [hoveredMarker, map]);

  useEffect(() => {
    if (!hasW3w) {
      clearMarker();
    } else {
      clearMarker().then(() => {
        drawMarker();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasW3w, what3words]);

  useEffect(() => {
    if (W3WMarker) {
      handleMarkerHover();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [W3WMarker]);

  return null;
};
